/**
 * Rule Operations
 *
 * Handles all rule-related database operations
 */

import type {
  Rule,
  RuleCreateData,
  ApplyRuleResult,
  RuleExistsResult,
  RuleMatchResult
} from '../../types';
import { query, queryOne, execute, transaction, saveDB } from '../../db';
import { rowToRule, type RuleRow } from '../mappers';
import { validateRule } from '../validation';
import { normalizePattern, isValidPattern, matchesPattern } from '../../rules/patternUtils';

/**
 * Get all rules ordered by creation date (newest first)
 */
export async function getAll(): Promise<Rule[]> {
    try {
      const rows = query<RuleRow>('SELECT * FROM rules ORDER BY created_at DESC');
      return rows.map(rowToRule);
    } catch (error) {
      console.error('[ruleOperations] Error loading rules:', error);
      return [];
    }
  }

  /**
   * Create rules from review decisions (bulk create)
   */
export async function createFromReview(decisions: Array<{
    description: string;
    category: string;
    person?: string | null;
  }>): Promise<{ created: number; skipped: number; skippedRules: any[]; createdRules: Rule[] }> {
    if (!Array.isArray(decisions) || decisions.length === 0) {
      return { created: 0, skipped: 0, skippedRules: [], createdRules: [] };
    }

    const createdRules: Rule[] = [];
    const skippedRules: any[] = [];

    for (const decision of decisions) {
      const { description, category, person } = decision;

      if (!description || !category) {
        skippedRules.push({ description, reason: 'Missing description or category' });
        continue;
      }

      // Extract pattern from description using utility function
      const pattern = normalizePattern(description);

      if (!isValidPattern(pattern)) {
        skippedRules.push({ description, reason: 'Pattern too short after normalization' });
        continue;
      }

      // Check if rule with this pattern already exists
      const existingCheck = await checkExists(pattern);
      if (existingCheck.exists) {
        skippedRules.push({ description, pattern, reason: 'Pattern already exists' });
        continue;
      }

      // Create new rule
      const result = await create(pattern, category, person, {
        normalizedName: null,
        markAsRecurring: false,
        recurringType: null,
        excludeFromAnalytics: false
      });

      if (result.success && result.rule) {
        createdRules.push(result.rule);
      } else {
        skippedRules.push({ description, pattern, reason: result.error || 'Failed to create rule' });
      }
    }

    return {
      created: createdRules.length,
      skipped: skippedRules.length,
      skippedRules,
      createdRules
    };
  }

  /**
   * Create a new rule
   */
export async function create(
    pattern: string,
    category: string,
    person: string | null = null,
    options: Partial<RuleCreateData> = {}
  ): Promise<{ success: boolean; rule?: Rule; error?: string }> {
    try {
      // Validate using utility function
      const validation = validateRule(pattern, category);
      if (!validation.valid) {
        const error = validation.errors[0].message;
        console.error('[ruleOperations] createRule validation failed:', { pattern, category });
        return { success: false, error };
      }

      const id = `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const now = new Date().toISOString();

      execute(
        `INSERT INTO rules (id, pattern, category, person, normalized_name, mark_as_recurring, recurring_type, exclude_from_analytics, enabled, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, 1, ?, ?)`,
        [
          id,
          pattern.toLowerCase(),
          category,
          person,
          options.normalizedName || null,
          options.markAsRecurring ? 1 : 0,
          options.recurringType || null,
          options.excludeFromAnalytics ? 1 : 0,
          now,
          now
        ]
      );

      await saveDB();

      const rule = queryOne<RuleRow>('SELECT * FROM rules WHERE id = ?', [id]);
      return { success: true, rule: rowToRule(rule!) };
    } catch (error) {
      console.error('[ruleOperations] Error creating rule:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Update an existing rule
   */
export async function update(
    ruleId: string,
    updates: Partial<RuleCreateData>
  ): Promise<{ success: boolean; rule?: Rule; error?: string }> {
    try {
      const existing = queryOne<RuleRow>('SELECT * FROM rules WHERE id = ?', [ruleId]);
      if (!existing) {
        return { success: false, error: 'Rule not found' };
      }

      const updated: any = { ...existing };
      if (updates.pattern !== undefined) updated.pattern = updates.pattern.toLowerCase();
      if (updates.category !== undefined) updated.category = updates.category;
      if (updates.person !== undefined) updated.person = updates.person;
      if (updates.normalizedName !== undefined) updated.normalized_name = updates.normalizedName;
      if (updates.markAsRecurring !== undefined) updated.mark_as_recurring = updates.markAsRecurring ? 1 : 0;
      if (updates.recurringType !== undefined) updated.recurring_type = updates.recurringType;
      if (updates.excludeFromAnalytics !== undefined) updated.exclude_from_analytics = updates.excludeFromAnalytics ? 1 : 0;
      updated.updated_at = new Date().toISOString();

      execute(
        `UPDATE rules SET pattern = ?, category = ?, person = ?, normalized_name = ?,
         mark_as_recurring = ?, recurring_type = ?, exclude_from_analytics = ?, updated_at = ?
         WHERE id = ?`,
        [
          updated.pattern,
          updated.category,
          updated.person,
          updated.normalized_name,
          updated.mark_as_recurring,
          updated.recurring_type,
          updated.exclude_from_analytics,
          updated.updated_at,
          ruleId
        ]
      );

      await saveDB();

      const rule = queryOne<RuleRow>('SELECT * FROM rules WHERE id = ?', [ruleId]);
      return { success: true, rule: rowToRule(rule!) };
    } catch (error) {
      console.error('[ruleOperations] Error updating rule:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Delete a rule
   */
export async function deleteRule(ruleId: string): Promise<{ success: boolean; error?: string }> {
    try {
      execute('DELETE FROM rules WHERE id = ?', [ruleId]);
      await saveDB();
      return { success: true };
    } catch (error) {
      console.error('[ruleOperations] Error deleting rule:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Apply a rule to existing transactions
   */
export async function applyToExisting(ruleId: string, forceApply: boolean = false): Promise<ApplyRuleResult> {
    try {
      const rule = queryOne<RuleRow>('SELECT * FROM rules WHERE id = ?', [ruleId]);
      if (!rule) {
        return { success: false, error: 'Rule not found' };
      }

      const pattern = rule.pattern.toLowerCase();
      const transactions = query<any>(
        'SELECT * FROM transactions WHERE LOWER(description) LIKE ? AND (category_source != ? OR ?)',
        [`%${pattern}%`, 'user', forceApply ? 1 : 0]
      );

      transaction(() => {
        for (const txn of transactions) {
          execute(
            `UPDATE transactions SET category = ?, person = ?, category_source = 'rule', updated_at = ?
             WHERE id = ?`,
            [rule.category, rule.person || txn.person, new Date().toISOString(), txn.id]
          );
        }
      });

      await saveDB();

      return {
        success: true,
        updatedCount: transactions.length,
        rule: rowToRule(rule)
      };
    } catch (error) {
      console.error('[ruleOperations] Error applying rule:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if a rule with the given pattern exists
   */
export async function checkExists(pattern: string): Promise<RuleExistsResult> {
    try {
      const rule = queryOne<RuleRow>('SELECT * FROM rules WHERE pattern = ?', [pattern.toLowerCase()]);
      return {
        exists: !!rule,
        rule: rule ? rowToRule(rule) : null
      };
    } catch (error) {
      console.error('[ruleOperations] Error checking rule existence:', error);
      return { exists: false, rule: null };
    }
  }

  /**
   * Apply rules to a transaction description and return first match
   */
export async function applyToTransaction(description: string): Promise<RuleMatchResult | null> {
    try {
      const rules = await getAll();

      for (const rule of rules) {
        if (rule.enabled && matchesPattern(description, rule.pattern)) {
          return {
            category: rule.category,
            person: rule.person
          };
        }
      }

      return null;
    } catch (error) {
      console.error('[ruleOperations] Error applying rules to transaction:', error);
      return null;
    }
}

/**
 * Delete all rules
 */
export async function deleteAll(): Promise<boolean> {
  try {
    execute('DELETE FROM rules');
    await saveDB();
    return true;
  } catch (error) {
    console.error('[ruleOperations] Error deleting all rules:', error);
    return false;
  }
}
